import React, { useEffect, useState } from "react";
import { useNavigate, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getWorkers } from "../../redux/features/workerSlice";
import { fetchClientsAsync } from "../../redux/features/clientSlice";
import { getCompany } from "../../redux/features/authSlice";
import { getCompanySlugFromUrl, getRoutePath } from "../../utils/routingUtils";
import { toast } from "react-toastify";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { usePageTracking } from "../../hooks/usePageTracking";
import Layout from '../../components/layout/AdminLayout';
import BusinessTypeProvider from '../../components/BusinessTypeProvider';
import DashboardHome from './DashboardHome';
import JobsDashboard from '../../components/dashboard/JobsDashboard';
import ClientsView from '../../components/views/ClientsView';
import ArchiveView from '../../components/views/ArchiveView';
import WorkersView from '../../components/views/actionview/WorkersView';
import PartnersView from '../../components/views/actionview/PartnersView';
import ServicesView from '../../components/views/actionview/ServicesView';
import SparePartsView from '../../components/dashboard/SparePartsView';
import CompanyInfoView from '../../components/views/companyview/CompanyInfoView';
import InventoryView from '../../components/views/companyview/InventoryView';
import InventoryInputsView from '../../components/views/companyview/InventoryInputsView';
import InventoryOutputsView from '../../components/views/companyview/InventoryOutputsView';
import WithdrawnItemsView from '../../components/views/companyview/WithdrawnItemsView';
import CustomsView from '../../components/views/companyview/CustomsView';
import CalculationsView from '../../components/views/companyview/CalculationsView';
import ReversiView from '../../components/views/companyview/ReversiView';
import SparePartInputsView from '../../components/views/companyview/SparePartInputsView';
import SparePartOutputsView from '../../components/views/companyview/SparePartOutputsView';
import AssetWriteOffView from '../../components/views/companyview/AssetWriteOffView';
import WebShopView from '../../components/views/companyview/WebShopView';
import AdminVehiclesView from '../../components/views/companyview/AdminVehiclesView';
import NotificationsView from '../../components/views/NotesView';
import FreeView from '../../components/views/packageview/FreeView';
import StarterView from '../../components/views/packageview/StarterView';
import StandardView from '../../components/views/packageview/StandardView';
import BusinessView from '../../components/views/packageview/BusinessView';
import PremiumView from '../../components/views/packageview/PremiumView';
import TutorialsView from '../../components/views/packageview/TutorialsView';
import LoyaltyView from '../../components/views/marketing/LoyaltyView';
import EmailView from '../../components/views/marketing/EmailView';
import SMSView from '../../components/views/marketing/SMSView';
import ViberView from '../../components/views/marketing/ViberView';
import WhatsAppView from '../../components/views/marketing/WhatsAppView';
import VideoUputstvaView from '../../components/views/VideoUputstvaView';
import SuperAdminDashboard from '../../components/views/superadmin/SuperAdminDashboard';
import SuperAdminAlerts from '../../components/views/superadmin/SuperAdminAlerts';
import CompaniesListView from '../../components/views/superadmin/CompaniesListView';
import BusinessContactsView from '../../components/views/superadmin/BusinessContactsView';
import CampaignsView from '../../components/views/superadmin/CampaignsView';
import EmailGroupsView from '../../components/views/superadmin/EmailGroupsView';
import PhoneGroupsView from '../../components/views/superadmin/PhoneGroupsView';
import AnalyticsView from '../../components/views/superadmin/AnalyticsView';
import MarketingConsentView from '../../components/views/superadmin/MarketingConsentView';
import FaultReportsView from '../../components/views/superadmin/FaultReportsView';
import SuperAdminInvoicingView from '../../components/views/superadmin/SuperAdminInvoicingView';
import JobAnalyticsView from '../../components/views/companyview/JobAnalyticsView';
import StatusView from '../../components/views/actionview/StatusView';
import KvarxMarketplaceView from '../../components/views/actionview/KvarxMarketplaceView';
import KvarxArchiveView from '../../components/views/actionview/KvarxArchiveView';
import AIBusinessView from '../../components/views/actionview/AIBusinessView';
import AIImportView from '../../components/views/aiview/AIImportView';
import AIExportView from '../../components/views/aiview/AIExportView';
import AISettingsView from '../../components/views/aiview/AISettingsView';
import UserManagementView from '../../components/views/profileview/UserManagementView';
import MyProfileView from '../../components/views/profileview/MyProfileView';
import WorkerDashboard from '../../components/views/WorkerDashboard';
import WorkerVehicleView from '../../components/views/worker/WorkerVehicleView';
import InvoicesView from '../../components/views/paymentsview/InvoicesView';
import ErrorBoundary from '../../components/ErrorBoundary';
import { selectUserWithMemo } from "../../utils/selectorUtils";

// Component to protect routes based on worker/partner permissions
const ProtectedRoute = ({ children, requiredPermission }) => {
  const userTypeFromStorage = localStorage.getItem('userType');
  const workerPermissions = JSON.parse(localStorage.getItem('workerPermissions') || '{}');
  const partnerPermissions = JSON.parse(localStorage.getItem('partnerPermissions') || '{}');
  const isWorker = userTypeFromStorage === 'worker';
  const isPartner = userTypeFromStorage === 'partner';
  
  // If not a worker or partner, allow access (company users have full access)
  if (!isWorker && !isPartner) {
    return children;
  }
  
  // Use appropriate permissions based on user type
  const permissions = isPartner ? partnerPermissions : workerPermissions;
  
  // If worker/partner doesn't have required permission, redirect to dashboard home
  if (requiredPermission && !permissions[requiredPermission]) {
    const companySlug = getCompanySlugFromUrl();
    return <Navigate to={companySlug ? `/${companySlug}/dashboard` : "/dashboard"} replace />;
  }
  
  return children;
};

// Component to handle redirect when non-superadmin tries to access superadmin route
const SuperAdminRedirect = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if user is logged in
    const profile = localStorage.getItem('profile');
    
    if (!profile) {
      // User is not logged in, redirect to login
      toast.error('Morate biti prijavljeni kao super admin da biste pristupili ovoj sekciji.');
      setTimeout(() => {
        navigate('/auth?role=company&type=login', { replace: true });
      }, 1500);
    } else {
      // User is logged in but not super admin, redirect to dashboard home
      toast.error('Nemate pristup Super Admin sekciji. Samo super admin korisnici mogu pristupiti ovoj stranici.');
      setTimeout(() => {
        const companySlug = getCompanySlugFromUrl();
        navigate(companySlug ? `/${companySlug}/dashboard` : '/dashboard', { replace: true });
      }, 1500);
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-white">Preusmeravanje...</p>
      </div>
    </div>
  );
};

/** Stari linkovi Plaćanja (virman / kartica / PayPal) vode na fakture. */
const RedirectToInvoices = () => {
  const loc = useLocation();
  return <Navigate to={getRoutePath('payments/fakture', loc.pathname)} replace />;
};

const Dashboard = ({ companySlug, accessData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Start as false, will be set to true if valid
  const user = useSelector(selectUserWithMemo);
  
  // Track admin dashboard visits
  const profile = localStorage.getItem('profile');
  let userEmail = null;
  if (profile) {
    try {
      const profileData = JSON.parse(profile);
      userEmail = profileData?.result?.email || profileData?.email;
    } catch (e) {}
  }

  usePageTracking('admin', {
    email: userEmail,
    skipTracking: !isAuthenticated || loading
  });
  
  // Load initial data when Dashboard mounts and user is authenticated
  // Load data immediately when component mounts, don't wait for loading state
  // BUT: If companySlug is provided, wait for it to be available (from TenantDashboard verification)
  useEffect(() => {
    // Check if user is authenticated from localStorage (faster than waiting for state)
    const profile = localStorage.getItem("profile");
    const isAuth = !!profile;

    if (companySlug && !accessData) {
      const currentCompanySlug = localStorage.getItem('currentCompanySlug');
      const currentCompanyId = localStorage.getItem('currentCompanyId');
      if (!currentCompanySlug || !currentCompanyId) return;
    }

    const currentPath = location?.pathname || window.location.pathname;
    const isJobsRoute = currentPath.includes('/dashboard/jobs');

    if (isAuth) {
      // Jobs: each route (DashboardHome, ClientsView, JobsDashboard, …) loads jobs as needed.
      // Avoid duplicate GET /api/jobs here + child (was 2–3 requests within milliseconds).
      if (!isJobsRoute) dispatch(getWorkers());
      if (!isJobsRoute) dispatch(fetchClientsAsync());
    }
  }, [dispatch, companySlug, accessData, location?.pathname]);

  // Osveži profil sa servera (uključujući package) da sidebar i AI provere vide ažuran paket
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    const profileStr = localStorage.getItem('profile');
    if (!profileStr) return;

    let shouldLoadCompany = userType === 'company';
    if (!shouldLoadCompany && (userType === 'worker' || userType === 'partner')) {
      try {
        const perms = JSON.parse(
          localStorage.getItem(userType === 'partner' ? 'partnerPermissions' : 'workerPermissions') || '{}'
        );
        shouldLoadCompany = !!(
          perms.canViewAllJobs &&
          perms.canEditJobs &&
          perms.canDeleteJobs &&
          perms.canEditClients
        );
      } catch {
        shouldLoadCompany = false;
      }
    }
    if (!shouldLoadCompany) return;

    try {
      const data = JSON.parse(profileStr);
      if (data?.result?.role === 'superadmin') return;
    } catch {
      return;
    }
    dispatch(getCompany());
  }, [dispatch]);

  // Check for super admin role and redirect if needed
  useEffect(() => {
    // Check from Redux state
    const isSuperAdminRedux = user?.result?.role === 'superadmin';
    
    // Check from localStorage as fallback
    const profileStr = localStorage.getItem('profile');
    let isSuperAdminStorage = false;
    if (profileStr) {
      try {
        const profileData = JSON.parse(profileStr);
        isSuperAdminStorage = profileData?.result?.role === 'superadmin';
      } catch (e) {}
    }
    
    const isSuperAdmin = isSuperAdminRedux || isSuperAdminStorage;
    
    // If super admin and not already on superadmin route, redirect
    if (isSuperAdmin && !loading && isAuthenticated) {
      const currentPath = window.location.pathname;
      
      // Don't redirect if already on a valid super admin route
      const validSuperAdminRoutes = ['superadmin', 'kompanije', 'poslovanje', 'kampanje', 'mobilni-telefoni', 'mejl-adrese', 'marketing-saglasnosti', 'prijave-kvarova', 'analitika', 'fakturisanje', 'notifications'];
      const isOnValidRoute = validSuperAdminRoutes.some((route) => {
        const legacyRoute = `/dashboard/${route}`;
        if (currentPath === legacyRoute || currentPath.startsWith(`${legacyRoute}/`)) {
          return true;
        }
        return new RegExp(`^/[^/]+/dashboard/${route}(/|$)`).test(currentPath);
      });
      
      if (!isOnValidRoute && currentPath.includes('/dashboard')) {
        navigate('/dashboard/superadmin', { replace: true });
      }
    }
  }, [user, loading, isAuthenticated, navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      const profile = localStorage.getItem("profile");
      if (!profile) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      try {
        const profileData = JSON.parse(profile);
        if (profileData.token) {
          const token = profileData.token;
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));
          const { exp } = JSON.parse(jsonPayload);
          const expired = Date.now() >= exp * 1000;
          if (expired) {
            setIsAuthenticated(false);
            setLoading(false);
            localStorage.removeItem('profile');
            localStorage.removeItem('token');
            localStorage.removeItem('lastActive');
            sessionStorage.clear();
            return;
          }
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }
        setIsAuthenticated(false);
        setLoading(false);
        return;
      } catch (error) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
    };

    checkAuth();

    // Set up periodic check every minute
    const intervalId = setInterval(checkAuth, 60000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Also check authentication when user state changes in Redux
  // This is a fallback to ensure authentication is set if Redux updates
  useEffect(() => {
    const profile = localStorage.getItem("profile");
    if (profile) {
      try {
        const profileData = JSON.parse(profile);
        if (profileData.token) {
          // Validate token is not expired
          try {
            const token = profileData.token;
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const { exp } = JSON.parse(jsonPayload);
            const expired = Date.now() >= exp * 1000;
            
            if (!expired) {
              setIsAuthenticated(true);
              setLoading(false);
              return;
            }
          } catch (tokenError) {}
        }
      } catch (error) {}
    }
    if (user) {
      setIsAuthenticated(true);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      const profile = localStorage.getItem("profile");
      if (profile) {
        try {
          const profileData = JSON.parse(profile);
          if (profileData.token) {
            try {
              const token = profileData.token;
              const base64Url = token.split('.')[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
              }).join(''));
              const { exp } = JSON.parse(jsonPayload);
              const expired = Date.now() >= exp * 1000;
              if (!expired) {
                setIsAuthenticated(true);
                return;
              }
            } catch (tokenError) {}
          }
        } catch (error) {}
      }
      const userType = localStorage.getItem('userType');
      const redirectRole = userType === 'worker' ? 'worker' : 'company';
      setTimeout(() => {
        navigate(`/auth?role=${redirectRole}&type=login`, { replace: true });
      }, 100);
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // Ako nije autentifikovan, ne renderujemo ništa dok nas efekat ne preusmeri
  // But also check if trying to access superadmin route without auth
  if (!isAuthenticated) {
    const currentPath = window.location.pathname;
    if (currentPath.includes('/dashboard/superadmin')) {
      // Trying to access superadmin without auth - redirect immediately
      setTimeout(() => {
        navigate('/auth?role=company&type=login', { replace: true });
      }, 0);
    }
    return <LoadingSpinner />;
  }

  // SECURITY: Strict authentication check - userType is the primary source of truth
  const userTypeFromStorage = localStorage.getItem('userType');
  const profileStr = localStorage.getItem('profile');
  let profileData = null;
  try {
    profileData = profileStr ? JSON.parse(profileStr) : null;
  } catch (e) {}

  // Additional validation: check profile structure to ensure userType matches actual data
  const hasWorkerStructure = profileData?.result?.firstName && profileData?.result?.lastName;
  const hasCompanyStructure = profileData?.result?.companyName;
  
  // Determine user type with strict validation
  let isWorker = false;
  let isPartner = false;
  
  if (userTypeFromStorage === 'worker') {
    // If userType is worker, validate it has worker structure and NOT company structure
    if (hasWorkerStructure && !hasCompanyStructure) {
      isWorker = true;
    }
  } else if (userTypeFromStorage === 'partner') {
    // Partners have same privileges as workers for now
    // If userType is partner, treat as worker
    isPartner = true;
    isWorker = true; // Treat partner as worker for dashboard routing
  } else if (userTypeFromStorage === 'company' || !userTypeFromStorage) {
    // If userType is company or not set, validate it has company structure
    if (hasWorkerStructure && !hasCompanyStructure) {
      isWorker = true;
    }
  }
  
  // Get permissions from localStorage (for workers and partners)
  const workerPermissions = JSON.parse(localStorage.getItem('workerPermissions') || '{}');
  const partnerPermissions = JSON.parse(localStorage.getItem('partnerPermissions') || '{}');
  // Use appropriate permissions based on user type
  const permissions = isPartner ? partnerPermissions : workerPermissions;
  const canViewAllJobs = permissions.canViewAllJobs || false;
  const canEditJobs = permissions.canEditJobs || false;
  const canDeleteJobs = permissions.canDeleteJobs || false;
  const canEditClients = permissions.canEditClients || false;
  const canViewWorkers = permissions.canViewWorkers || false;
  const canViewClients = permissions.canViewClients || false;
  
  // Check if worker/partner has "Samo dodeljeni poslovi" permission (minimal access)
  // This means: no canViewAllJobs, no canViewWorkers, no canViewClients
  const hasOnlyAssignedJobsAccess = isWorker && !canViewAllJobs && !canViewWorkers && !canViewClients;

  // Worker Dashboard - ONLY show if user is confirmed worker AND has only assigned jobs access
  // If worker has canViewAllJobs or any other section access, they see admin dashboard
  // EXCEPTION: allow minimal-access workers to use certain extra routes (e.g. vehicle section)
  // Use location from React Router for more accurate path checking
  const currentPath = location.pathname;
  // Check if we're on the vehicle route - handle both slug and non-slug formats
  const isWorkerVehicleRoute =
    currentPath.includes('/dashboard/worker/vehicle') ||
    currentPath.endsWith('/worker/vehicle') ||
    currentPath.includes('/worker/vehicle/') ||
    currentPath === '/worker/vehicle';

  // If worker has only assigned jobs access AND is NOT on vehicle route, show WorkerDashboard
  // Otherwise, render AdminLayout with routes (which includes vehicle route)
  if (hasOnlyAssignedJobsAccess && !isWorkerVehicleRoute) {
    // Note: ErrorBoundary must be inside Redux Provider (which is in index.js)
    // So we don't need to wrap it again here
    return (
      <BusinessTypeProvider>
        <ErrorBoundary>
          <WorkerDashboard />
        </ErrorBoundary>
      </BusinessTypeProvider>
    );
  }
  
  // Ako nema korisnika u Redux stanju ali imamo localstorage profil, 
  // to može biti stanje nakon refresha - svejedno prikazujemo dashboard
  if (!user && !loading && isAuthenticated) {
    const profileStr = localStorage.getItem('profile');
    let profileData = null;
    try {
      profileData = profileStr ? JSON.parse(profileStr) : null;
    } catch (e) {}
    const isSuperAdminFromStorage = profileData?.result?.role === 'superadmin';

    if (isSuperAdminFromStorage) {
      return (
        <BusinessTypeProvider>
          <Layout>
            <SuperAdminAlerts />
            <Routes>
              <Route path="kompanije" element={<CompaniesListView />} />
              <Route path="poslovanje" element={<BusinessContactsView />} />
              <Route path="kampanje" element={<CampaignsView />} />
              <Route path="mobilni-telefoni" element={<PhoneGroupsView />} />
              <Route path="mejl-adrese" element={<EmailGroupsView />} />
              <Route path="marketing-saglasnosti" element={<MarketingConsentView />} />
              <Route path="prijave-kvarova" element={<FaultReportsView />} />
              <Route path="analitika" element={<AnalyticsView />} />
              <Route path="fakturisanje" element={<SuperAdminInvoicingView />} />
              <Route path="notifications" element={<NotificationsView />} />
              <Route path="superadmin" element={<SuperAdminDashboard />} />
              <Route path="" element={<Navigate to="/dashboard/superadmin" replace />} />
              <Route path="*" element={<Navigate to="/dashboard/superadmin" replace />} />
            </Routes>
          </Layout>
        </BusinessTypeProvider>
      );
    }

    return (
      <BusinessTypeProvider>
        <Layout>
          <Routes>
            <Route path="" element={<DashboardHome />} />
            <Route path="jobs/*" element={<JobsDashboard />} />
            <Route path="kvarx" element={<KvarxMarketplaceView />} />
            <Route path="kvarx-archive" element={<KvarxArchiveView />} />
            <Route path="status" element={<StatusView />} />
            <Route path="ai-business" element={<AIBusinessView />} />
            <Route path="ai-import" element={
              <ProtectedRoute requiredPermission="canViewClients">
                <AIImportView />
              </ProtectedRoute>
            } />
            <Route path="ai-export" element={<AIExportView />} />
            <Route path="ai-settings" element={<AISettingsView />} />
            <Route path="services" element={<ServicesView title="Kategorije i usluge" />} />
            <Route path="clients/*" element={
              <ProtectedRoute requiredPermission="canViewClients">
                <ClientsView />
              </ProtectedRoute>
            } />
            <Route path="archive" element={<ArchiveView />} />
            <Route path="workers/*" element={
              <ProtectedRoute requiredPermission="canViewWorkers">
                <WorkersView />
              </ProtectedRoute>
            } />
            <Route path="notifications" element={<NotificationsView />} />
            <Route path="company/webshop" element={<WebShopView />} />
            <Route path="company/info" element={<CompanyInfoView />} />
            <Route path="company/inventory" element={<InventoryView />} />
            <Route path="company/inventory/inputs" element={<InventoryInputsView />} />
            <Route path="company/inventory/outputs" element={<InventoryOutputsView />} />
            <Route path="company/inventory/withdrawn" element={<WithdrawnItemsView fixedSourceType="INVENTORY_ITEM" stockKind="WAREHOUSE" />} />
            <Route path="company/inventory/customs" element={<CustomsView />} />
            <Route path="company/inventory/calculations" element={<CalculationsView />} />
            <Route path="company/inventory/reversi" element={<ReversiView />} />
            <Route path="company/inventar" element={<InventoryView stockKind="ASSET" />} />
            <Route path="company/inventar/inputs" element={<InventoryInputsView stockKind="ASSET" />} />
            <Route path="company/inventar/withdrawn" element={<AssetWriteOffView />} />
            <Route path="vehicles" element={<AdminVehiclesView />} />
            <Route path="marketing/loyalty" element={<LoyaltyView />} />
            <Route path="marketing/email" element={<EmailView />} />
            <Route path="marketing/sms" element={<SMSView />} />
            <Route path="marketing/viber" element={<ViberView />} />
            <Route path="marketing/whatsapp" element={<WhatsAppView />} />
            <Route path="tutorijali/video" element={<VideoUputstvaView type="video" />} />
            <Route path="tutorijali/slike" element={<VideoUputstvaView type="image" />} />
            <Route path="tutorijali/kako-koristiti-app" element={<TutorialsView />} />
            <Route path="packages/free" element={<FreeView />} />
            <Route path="packages/starter" element={<StarterView />} />
            <Route path="packages/standard" element={<StandardView />} />
            <Route path="packages/business" element={<BusinessView />} />
            <Route path="packages/premium" element={<PremiumView />} />
            <Route path="spare-parts" element={<SparePartsView />} />
            <Route path="spare-parts/inputs" element={<SparePartInputsView />} />
            <Route path="spare-parts/outputs" element={<SparePartOutputsView />} />
            <Route path="worker/vehicle" element={<WorkerVehicleView />} />
            <Route path="profile" element={<MyProfileView />} />
            <Route path="user-management" element={<UserManagementView />} />
            <Route path="payments/wire-transfer" element={<RedirectToInvoices />} />
            <Route path="payments/fakture" element={<InvoicesView />} />
            <Route path="payments/card" element={<RedirectToInvoices />} />
            <Route path="payments/paypal" element={<RedirectToInvoices />} />
            <Route path="superadmin" element={<SuperAdminRedirect />} />
            <Route path="*" element={<Navigate to={companySlug ? `/${companySlug}/dashboard` : "/dashboard"} replace />} />
          </Routes>
        </Layout>
      </BusinessTypeProvider>
    );
  }

  // Check if user is super admin
  const isSuperAdmin = user?.result?.role === 'superadmin';
  
  if (isSuperAdmin) {
    return (
      <BusinessTypeProvider>
        <Layout>
          <SuperAdminAlerts />
          <Routes>
            <Route path="kompanije" element={<CompaniesListView />} />
            <Route path="poslovanje" element={<BusinessContactsView />} />
            <Route path="kampanje" element={<CampaignsView />} />
            <Route path="mobilni-telefoni" element={<PhoneGroupsView />} />
            <Route path="mejl-adrese" element={<EmailGroupsView />} />
            <Route path="marketing-saglasnosti" element={<MarketingConsentView />} />
            <Route path="prijave-kvarova" element={<FaultReportsView />} />
            <Route path="analitika" element={<AnalyticsView />} />
            <Route path="fakturisanje" element={<SuperAdminInvoicingView />} />
            <Route path="notifications" element={<NotificationsView />} />
            <Route path="superadmin" element={<SuperAdminDashboard />} />
            <Route path="" element={<Navigate to="/dashboard/superadmin" replace />} />
            <Route path="*" element={<Navigate to="/dashboard/superadmin" replace />} />
          </Routes>
        </Layout>
      </BusinessTypeProvider>
    );
  }

  // Standardni prikaz za autentifikovane korisnike (company users i workers sa canViewAllJobs)
  return (
    <BusinessTypeProvider>
      <Layout>
        <Routes>
          <Route path="" element={<DashboardHome />} />
          <Route path="jobs/*" element={<JobsDashboard />} />
          <Route path="kvarx" element={<KvarxMarketplaceView />} />
          <Route path="kvarx-archive" element={<KvarxArchiveView />} />
          <Route path="status" element={<StatusView />} />
          <Route path="ai-business" element={<AIBusinessView />} />
          <Route path="ai-import" element={
            <ProtectedRoute requiredPermission="canViewClients">
              <AIImportView />
            </ProtectedRoute>
          } />
          <Route path="ai-export" element={<AIExportView />} />
          <Route path="ai-settings" element={<AISettingsView />} />
          <Route path="services" element={<ServicesView title="Kategorije i usluge" />} />
          <Route path="clients/*" element={
            <ProtectedRoute requiredPermission="canViewClients">
              <ClientsView />
            </ProtectedRoute>
          } />
          <Route path="archive" element={<ArchiveView />} />
          <Route path="analitika" element={<JobAnalyticsView />} />
          <Route path="workers/*" element={
            <ProtectedRoute requiredPermission="canViewWorkers">
              <WorkersView />
            </ProtectedRoute>
          } />
          <Route path="partners" element={<PartnersView />} />
          <Route path="notifications" element={<NotificationsView />} />
          <Route path="company/webshop" element={<WebShopView />} />
          <Route path="company/info" element={<CompanyInfoView />} />
          <Route path="company/inventory" element={<InventoryView />} />
          <Route path="company/inventory/inputs" element={<InventoryInputsView />} />
          <Route path="company/inventory/outputs" element={<InventoryOutputsView />} />
          <Route path="company/inventory/withdrawn" element={<WithdrawnItemsView fixedSourceType="INVENTORY_ITEM" stockKind="WAREHOUSE" />} />
          <Route path="company/inventory/customs" element={<CustomsView />} />
          <Route path="company/inventory/calculations" element={<CalculationsView />} />
          <Route path="company/inventory/reversi" element={<ReversiView />} />
          <Route path="company/inventar" element={<InventoryView stockKind="ASSET" />} />
          <Route path="company/inventar/inputs" element={<InventoryInputsView stockKind="ASSET" />} />
          <Route path="company/inventar/withdrawn" element={<AssetWriteOffView />} />
          <Route path="vehicles" element={<AdminVehiclesView />} />
          <Route path="marketing/loyalty" element={<LoyaltyView />} />
          <Route path="marketing/email" element={<EmailView />} />
          <Route path="marketing/sms" element={<SMSView />} />
          <Route path="marketing/viber" element={<ViberView />} />
          <Route path="marketing/whatsapp" element={<WhatsAppView />} />
          <Route path="tutorijali/video" element={<VideoUputstvaView type="video" />} />
          <Route path="tutorijali/slike" element={<VideoUputstvaView type="image" />} />
          <Route path="tutorijali/kako-koristiti-app" element={<TutorialsView />} />
          <Route path="profile" element={<MyProfileView />} />
          <Route path="packages/free" element={<FreeView />} />
          <Route path="packages/starter" element={<StarterView />} />
          <Route path="packages/standard" element={<StandardView />} />
          <Route path="packages/business" element={<BusinessView />} />
          <Route path="packages/premium" element={<PremiumView />} />
          <Route path="spare-parts" element={<SparePartsView />} />
          <Route path="spare-parts/inputs" element={<SparePartInputsView />} />
          <Route path="spare-parts/outputs" element={<SparePartOutputsView />} />
          <Route path="worker/vehicle" element={<WorkerVehicleView />} />
          <Route path="user-management" element={<UserManagementView />} />
          <Route path="payments/wire-transfer" element={<RedirectToInvoices />} />
          <Route path="payments/fakture" element={<InvoicesView />} />
          <Route path="payments/card" element={<RedirectToInvoices />} />
          <Route path="payments/paypal" element={<RedirectToInvoices />} />
          <Route path="superadmin" element={<SuperAdminRedirect />} />
          <Route path="*" element={<Navigate to={companySlug ? `/${companySlug}/dashboard` : "/dashboard"} replace />} />
        </Routes>
      </Layout>
    </BusinessTypeProvider>
  );
};

export default Dashboard;
